
import { defineComponent, PropType } from 'vue'
import SocialBannerNew from '@/components/library/AsahiSocialBanner.vue'
import AsahiLogoNew from '@/components/library/assets/AsahiLogoNew.vue'
import { Collapse } from 'bootstrap'
import { hasPermission } from '@/utils/permissions'
import { mapGetters } from 'vuex'
import { AuthMutationTypes } from '@/store/modules/auth/interfaces/mutation-types'
import angleCircleUp from '@/assets/icons/library/angle-circle-up.svg'
import { IPortalHardCodeLink } from '@/store/modules/links/interfaces/IPortalHardCodeLink'
import AsahiTypography from '@/components/library/Typography.vue'

interface FooterLink {
  label: string
  href?: string
  link?: string
  target?: string
  name?: string
  permission?: string
}

interface FooterSection {
  name?: string
  title?: string
  label?: string
  links?: FooterLink[]
  children?: FooterLink[]
}

interface HeaderData {
  label: string;
  children: any[];
  name: string;
}

export default defineComponent({
  name: 'AsahiFooter',
  components: { AsahiLogoNew, SocialBannerNew, AsahiTypography },
  emits: ['languagechange'],
  props: {
    headerData: {
      type: Object as PropType<HeaderData[]>,
      required: true
    },
    language: {
      type: Array as () => string[],
      required: true
    },
    activeLanguage: {
      type: String,
      required: true
    },
    footerData: {
      type: Object as () => FooterSection[],
      required: true
    },
    brandData: {
      type: Object as () => FooterLink[],
      required: true
    },
    cookies: {
      type: Object as () => FooterLink[],
      required: true
    },
    termsOfUse: {
      type: Array as () => FooterLink[],
      required: true
    },
    privacyPolicy: {
      type: Array as () => FooterLink[],
      required: true
    }
  },

  data: () => ({
    angleCircleUp,
    openLanguage: false,
    combinedFooterData: [] as FooterSection[]
  }),
  computed: {
    ...mapGetters('main', ['getPermissions']),
    user () {
      return this.$store.state.auth.user
    },
    isLoggedIn () {
      return this.user.isLoggedIn
    },

    allPermission () {
      return this.user.allAvailablePermission
    },
    isOutletUser () {
      const shipToIdFromStorage = localStorage.getItem('shipToId')
      return this.user?.permissionSchema?.some((company) =>
        company.shipToId === shipToIdFromStorage &&
        company.rpDetails?.some((rp) => rp.customerType === 'Outlets')
      )
    },
    portalHardCodeLink (): IPortalHardCodeLink[] {
      return this.$store.state.links.portalHardCodeLink?.results ?? []
    },
    isUserManagementPage () {
      return this.$route.params.tab === 'management'
    },
    footerBottomLinks () {
      let footerBottomLink = [] as IPortalHardCodeLink[]
      if (this.portalHardCodeLink.length > 0) {
        footerBottomLink = this.portalHardCodeLink.filter((lnk) => lnk.type === 'About Us')
      }
      return footerBottomLink
    }
  },
  methods: {
    scrollToTop () {
      window.scrollTo(0, 0)
    },
    navigateToPath (link: FooterLink) {
      this.$gtm.trackEvent({ ecommerce: null })
      this.$gtm.trackEvent({
        event: 'click_interaction',
        action: link.name === 'My Favorites' ? 'Favourites' : link.name === 'Order History' ? 'Order history' : link.name,
        component: 'footer'
      })

      this.$navigate(link.href || link.link || '')
    },
    trackExternalLink (link: { label?: string, name?: string, href?: string }) {
      this.$gtm.trackEvent({ ecommerce: null })
      this.$gtm.trackEvent({
        event: 'click_interaction',
        action: link?.name || '',
        component: 'footer'
      })
    },

    closeLangAccordion () {
      const myCollapse = document.getElementById('asahi-bs-footer-accordion-collapse-language')
      if (myCollapse?.classList.contains('show')) {
        Collapse(myCollapse, {
          toggle: true
        })
      }
    },

    openLanguageDropdown () {
      this.openLanguage = !this.openLanguage
    },
    changeLanguage (lang: string) {
      if (lang !== this.activeLanguage) {
        this.$emit('languagechange', lang)
        this.setMagentoStore(lang)
      }
    },
    getFullLanguageName (code: string): string {
      const languageMap: { [key: string]: string } = { en: 'English', it: 'Italiano' }
      return languageMap[code] || code
    },
    combineFooterData () {
      const orderedData: FooterSection[] = []
      const catalogSection = this.footerData.find(section => section.name === 'products_catalogue')
      const ordersSection = this.footerData.find(section => section.name === 'Orders')
      const otherSections = this.footerData.filter(section => section.name !== 'Orders' && section.name !== 'Create an order' && section.name !== 'products_catalogue')
      const myAccountSection = this.newMenu().find(section => section.name === 'profile')

      if (catalogSection) {
        orderedData.push(catalogSection)
      }
      if (ordersSection) {
        orderedData.push(ordersSection)
      }

      if (this.brandData) {
        orderedData.push({
          title: this.$t('Our Brands'),
          links: this.brandData
        })
      }

      otherSections.forEach((section, index) => {
        if (index === 1) {
          if (myAccountSection) {
            orderedData.push(myAccountSection)
          }
        }
        orderedData.push(section)
      })

      if (myAccountSection && !orderedData.includes(myAccountSection) && orderedData.length < 4) {
        orderedData.push(myAccountSection)
      }

      if (!this.isLoggedIn) {
        const supportSec: FooterSection =
          {
            label: this.$t('Support'),
            name: 'Support',
            children: [
              {
                label: this.$t('News and Articles'),
                href: '/news'
              },
              {
                label: this.$t('Contacts'),
                href: '/contact'
              },
              {
                label: this.$t('Become a partner'),
                href: '/become-partner'
              }
            ]
          }

        orderedData.push(supportSec)
      }

      this.combinedFooterData = this.isLoggedIn ? orderedData : orderedData.filter((item :any) => item.children && item.children.length > 0)
    },
    newMenu (): FooterSection[] {
      return [
        {
          label: this.$t('My account'),
          name: 'profile',
          children: this.user ? this.getFooterChildren() : []
        }
      ]
    },
    getFooterChildren () {
      const footerLinks: FooterLink[] = [
        {
          name: 'My Documents',
          label: this.$t('My Documents'),
          href: '/my-documents/documents',
          permission: 'My Documents'
        },
        {
          name: 'Reporting',
          label: this.$t('Reporting'),
          href: '/reporting',
          permission: 'Reporting'
        },
        {
          name: 'Contracts',
          label: this.$t('Contracts'),
          href: '/contracts',
          permission: 'Contracts'
        },
        {
          name: 'My Profile',
          label: this.$t('My Profile'),
          href: '/my-profile/details',
          permission: 'My Profile'
        }
      ]
      // If the user is a POSUser, filter out 'Contracts' from the footer links.
      const filteredLinks = this.isOutletUser
        ? footerLinks.filter(link => link.name === 'Reporting' || link.name === 'My Profile')
        : footerLinks
      return filteredLinks.filter((f) => {
        return f.permission && hasPermission(this.allPermission, this.getPermissions(), f.permission)
      })
    },
    menuNavigate (link: FooterLink) {
      if (
        link?.label === 'Academy' || link?.label === 'Formazione' ||
        link?.label === 'Pub Menu'
      ) {
        window.gigya.fidm.saml.initSSO({
          spName: link?.name,
          redirectURL: link.href
        })
      }
    },
    setMagentoStore (language: string) {
      if (this.user.country === 'SVK' || (this.user.country == null && window.location.hostname.includes('.sk'))) {
        localStorage.setItem('store', `LivePortal_SK_${language}`)
      }
      if (this.user.country === 'CZE' || (this.user.country == null && window.location.hostname.includes('.cz'))) {
        localStorage.setItem('store', `LivePortal_CZ_${language}`)
      }
      if (this.user.country === 'POL' || (this.user.country == null && window.location.hostname.includes('.cz'))) {
        localStorage.setItem('store', `LivePortal_PL_${language}`)
      }

      if (!(this.user.country == null || this.user.country === '') && !this.$store.state.auth.isLanguageLoaded) {
        this.$store.commit(AuthMutationTypes.SET_LANGUAGE_LOADED, true)
      }
    },
    isExternal (url:string) {
      return url?.includes('http')
    }
  },
  mounted () {
    if (this.footerData) {
      this.combineFooterData()
    }
  },
  watch: {
    footerData: {
      deep: true,
      handler (newVal) {
        if (newVal && newVal.length > 0) {
          this.combineFooterData()
        }
      }
    },
    brandData: {
      deep: true,
      handler (newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.combineFooterData()
        }
      }
    }
  }
})
